<script setup lang="ts">
import { computed, ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import SiteHeader from '@/components/SiteHeader.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import HomeLayout from '@/layouts/HomeLayout.vue';
import AddressQuoteForm from '@/components/AddressQuoteForm.vue';

interface Faq {
    question: string;
    answer: string;
}

interface Location {
    name: string;
    slug: string;
    state: string | null;
    meta_title: string | null;
    meta_description: string | null;
    body_content: string | null;
    faqs: Faq[] | null;
    service_areas: string[] | null;
    internal_links: string[] | null;
}

const props = defineProps<{
    location: Location;
    servicePageLinks?: string[];
    suburbLinks?: string[];
    jsonLdSchema?: string;
}>();

defineOptions({ layout: HomeLayout });

const pageTitle = computed(
    () =>
        props.location.meta_title ??
        `Lawn Mowing Service ${props.location.name} ${props.location.state ?? 'TX'} - Mow Managers`,
);
const pageDescription = computed(
    () =>
        props.location.meta_description ??
        `Affordable lawn mowing service in ${props.location.name}, ${props.location.state ?? 'TX'}. Weekly mowing, trimming, edging, and cleanup from trusted local pros.`,
);
const canonicalUrl = computed(
    () => `https://mowmanagers.com/${props.location.slug}`,
);

const fullH1 = computed(
    () => `Lawn Mowing Service in ${props.location.name}, ${props.location.state ?? 'TX'}`,
);

/**
 * Split the H1 into two lines — main in white, city part in green.
 */
const heroTitle = computed(() => {
    const full = fullH1.value;
    const cityPart = `in ${props.location.name}`;
    const idx = full.lastIndexOf(cityPart);

    if (idx > 0) {
        return {
            main: full.substring(0, idx).trim(),
            accent: full.substring(idx).trim(),
        };
    }

    return { main: full, accent: '' };
});

const heroSubtext = computed(
    () =>
        `Affordable & reliable lawn care for homes & small businesses in ${props.location.name}, ${props.location.state ?? 'TX'}.`,
);

/** Images for image+text sections. */
const sectionImages = [
    '/img/below-header-left.png',
    '/img/local-lawn-care.webp',
    '/img/below-header-middle.webp',
    '/img/below-header-right.webp',
];

interface HighlightItem {
    title: string;
    description: string;
}

interface ContentSection {
    heading: string;
    body: string;
    isCta: boolean;
    layout: 'image-text' | 'full-width' | 'highlight-cards';
    /** For highlight-cards: intro paragraphs before the list. */
    introHtml: string;
    /** For highlight-cards: extracted list items. */
    highlights: HighlightItem[];
}

/**
 * Split body_content by <h2> into structured sections with layout assignments.
 */
const contentSections = computed((): ContentSection[] => {
    if (!props.location.body_content) {
        return [];
    }

    const parts = props.location.body_content.split(/(?=<h2>)/);
    const sections: ContentSection[] = [];

    for (const part of parts) {
        const trimmed = part.trim();

        if (!trimmed) {
            continue;
        }

        const headingMatch = trimmed.match(/<h2>(.*?)<\/h2>/s);
        const heading = headingMatch
            ? headingMatch[1].replace(/<[^>]*>/g, '')
            : '';
        const body = headingMatch
            ? trimmed.substring(trimmed.indexOf('</h2>') + 5).trim()
            : trimmed;

        // Determine layout
        const headingLower = heading.toLowerCase();
        const textLength = body.replace(/<[^>]*>/g, '').length;
        const hasStrongItems = /<li>\s*<strong>/.test(body);

        let layout: ContentSection['layout'] = 'image-text';
        let introHtml = '';
        let highlights: HighlightItem[] = [];

        if (
            hasStrongItems &&
            (headingLower.includes('tips') ||
                headingLower.includes('why') ||
                headingLower.includes('choose'))
        ) {
            // Extract intro paragraphs (everything before <ul>)
            const ulIndex = body.indexOf('<ul>');

            if (ulIndex > 0) {
                introHtml = body.substring(0, ulIndex).trim();
            }

            // Extract list items as cards
            const itemMatches = [
                ...body.matchAll(
                    /<li>\s*<strong>(.*?)<\/strong>\s*[-–—]\s*(.*?)<\/li>/gs,
                ),
            ];

            if (itemMatches.length >= 3) {
                layout = 'highlight-cards';
                highlights = itemMatches.map((m) => ({
                    title: m[1].replace(/<[^>]*>/g, ''),
                    description: m[2].replace(/<[^>]*>/g, ''),
                }));
            }
        } else if (textLength > 1400) {
            layout = 'full-width';
        }

        sections.push({
            heading,
            body,
            isCta: false,
            layout,
            introHtml,
            highlights,
        });
    }

    // Flag the last section as CTA if it looks like one
    if (sections.length > 0) {
        const last = sections[sections.length - 1];

        if (
            last.heading.toLowerCase().includes('quote') ||
            last.heading.toLowerCase().includes('get started')
        ) {
            last.isCta = true;
        }
    }

    return sections;
});

const bodySections = computed(() =>
    contentSections.value.filter((s) => !s.isCta),
);

const ctaSection = computed(
    () => contentSections.value.find((s) => s.isCta) ?? null,
);

/**
 * Track image index separately so only image-text sections consume images.
 */
function getImageForSection(sectionIndex: number): string {
    let imageCount = 0;

    for (let i = 0; i < sectionIndex; i++) {
        if (bodySections.value[i].layout === 'image-text') {
            imageCount++;
        }
    }

    return sectionImages[imageCount % sectionImages.length];
}

/**
 * Track alternation only among image-text sections.
 */
function getImageTextIndex(sectionIndex: number): number {
    let count = 0;

    for (let i = 0; i < sectionIndex; i++) {
        if (bodySections.value[i].layout === 'image-text') {
            count++;
        }
    }

    return count;
}

const openFaqIndex = ref<number | null>(null);

function toggleFaq(index: number): void {
    openFaqIndex.value = openFaqIndex.value === index ? null : index;
}

/**
 * Combine service page links and suburb links into a single list for the
 * "Our [City] Services" section. These are built dynamically from the DB
 * relationships so every child page is always linked.
 */
const allInternalLinks = computed(() => {
    const links: string[] = [];

    if (props.servicePageLinks?.length) {
        links.push(...props.servicePageLinks);
    }

    if (props.suburbLinks?.length) {
        links.push(...props.suburbLinks);
    }

    return links;
});
</script>

<template>
    <div class="bg-white">
        <Head :title="pageTitle">
            <meta
                head-key="description"
                name="description"
                :content="pageDescription"
            />
            <link rel="canonical" :href="canonicalUrl" />
            <meta
                property="og:title"
                :content="pageTitle.includes('Mow Managers') ? pageTitle : `${pageTitle} - Mow Managers`"
            />
            <meta property="og:description" :content="pageDescription" />
            <meta property="og:type" content="website" />
            <meta property="og:url" :content="canonicalUrl" />
            <meta
                property="og:image"
                content="https://mowmanagers.com/img/hero.png"
            />
            <meta
                property="og:image:alt"
                content="Mow Managers lawn care service"
            />
        </Head>

        <!-- ==================== HERO + NAV ==================== -->
        <div class="relative">
            <SiteHeader transparent />

            <section
                class="relative flex min-h-[75vh] items-center bg-cover bg-center bg-no-repeat lg:min-h-[80vh]"
                :style="{ backgroundImage: `url('/img/hero.png')` }"
            >
                <div class="absolute inset-0 bg-black/50" />

                <div
                    class="relative z-10 mx-auto w-full max-w-7xl px-4 py-32 sm:px-6 lg:px-8 lg:py-40"
                >
                    <h1
                        class="text-5xl font-bold sm:text-6xl lg:text-[90px] lg:leading-[100px] xl:text-[110px] xl:leading-[114px]"
                    >
                        <span class="text-white">{{ heroTitle.main }}</span>
                        {{ ' ' }}<br />
                        <span class="text-mm-green">{{
                            heroTitle.accent
                        }}</span>
                    </h1>

                    <p class="mt-4 max-w-xl text-[18px] font-medium text-white">
                        {{ heroSubtext }}
                    </p>

                    <AddressQuoteForm
                        variant="hero"
                        input-id-prefix="hub-hero"
                    />
                </div>
            </section>
        </div>

        <!-- ==================== BELOW-HERO IMAGES ==================== -->
        <div
            class="relative z-20 -mt-10 hidden gap-4 px-2 md:grid md:grid-cols-4 lg:-mt-16 lg:gap-6 lg:px-4"
        >
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-left.png"
                    alt="Lawn mower on grass"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-2">
                <img
                    src="/img/below-header-middle.webp"
                    alt="Family enjoying lawn"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-right.webp"
                    alt="Professional mowing"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
        </div>

        <!-- ==================== BODY CONTENT SECTIONS ==================== -->
        <template v-for="(section, index) in bodySections" :key="index">
            <!-- ===== IMAGE + TEXT LAYOUT ===== -->
            <section
                v-if="section.layout === 'image-text'"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-mm-light'"
                class="py-20 lg:py-28"
            >
                <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <article
                        class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
                    >
                        <div
                            :class="
                                getImageTextIndex(index) % 2 === 1
                                    ? 'lg:order-2'
                                    : ''
                            "
                        >
                            <div class="overflow-hidden rounded-[20px]">
                                <img
                                    :src="getImageForSection(index)"
                                    :alt="`${section.heading} - Mow Managers ${location.name}`"
                                    class="h-[350px] w-full object-cover lg:h-[480px]"
                                    loading="lazy"
                                />
                            </div>
                        </div>

                        <div
                            :class="
                                getImageTextIndex(index) % 2 === 1
                                    ? 'lg:order-1'
                                    : ''
                            "
                        >
                            <p
                                class="text-[14px] font-extrabold tracking-[2.8px] text-mm-green uppercase"
                            >
                                {{ location.name }} Lawn Care
                            </p>
                            <h2
                                class="mt-2 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[48px] lg:leading-[56px]"
                            >
                                {{ section.heading }}
                            </h2>
                            <div
                                class="mt-4 h-[3px] w-12 bg-mm-green"
                                aria-hidden="true"
                            />
                            <div
                                class="prose mt-6 max-w-none prose-p:text-[16px] prose-p:font-medium prose-p:leading-[26px] prose-p:text-[#6d706d] prose-li:text-[16px] prose-li:font-medium prose-li:leading-[26px] prose-li:text-[#6d706d] prose-strong:text-mm-dark prose-ol:text-[#6d706d]"
                                v-html="section.body"
                            />
                        </div>
                    </article>
                </div>
            </section>

            <!-- ===== FULL-WIDTH TEXT LAYOUT (long sections) ===== -->
            <section
                v-else-if="section.layout === 'full-width'"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-mm-light'"
                class="py-20 lg:py-28"
            >
                <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div class="mb-8 text-center">
                        <p
                            class="text-[14px] font-extrabold tracking-[2.8px] text-mm-green uppercase"
                        >
                            {{ location.name }} Lawn Care
                        </p>
                        <h2
                            class="mt-2 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[48px] lg:leading-[56px]"
                        >
                            {{ section.heading }}
                        </h2>
                        <div
                            class="mx-auto mt-4 h-[3px] w-12 bg-mm-green"
                            aria-hidden="true"
                        />
                    </div>
                    <div
                        class="prose prose-lg mx-auto max-w-none prose-p:text-[17px] prose-p:font-medium prose-p:leading-[30px] prose-p:text-[#6d706d] prose-li:text-[17px] prose-li:font-medium prose-li:leading-[30px] prose-li:text-[#6d706d] prose-strong:text-mm-dark"
                        v-html="section.body"
                    />
                </div>
            </section>

            <!-- ===== HIGHLIGHT CARDS LAYOUT (tips, benefits) ===== -->
            <section
                v-else-if="section.layout === 'highlight-cards'"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-mm-light'"
                class="py-20 lg:py-28"
            >
                <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div class="mb-12 text-center">
                        <p
                            class="text-[14px] font-extrabold tracking-[2.8px] text-mm-green uppercase"
                        >
                            {{ location.name }} Lawn Care
                        </p>
                        <h2
                            class="mt-2 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[48px] lg:leading-[56px]"
                        >
                            {{ section.heading }}
                        </h2>
                        <div
                            class="mx-auto mt-4 h-[3px] w-12 bg-mm-green"
                            aria-hidden="true"
                        />
                    </div>

                    <div
                        v-if="section.introHtml"
                        class="prose prose-lg mx-auto mb-12 max-w-3xl text-center prose-p:text-[17px] prose-p:font-medium prose-p:leading-[30px] prose-p:text-[#6d706d]"
                        v-html="section.introHtml"
                    />

                    <div
                        class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8"
                    >
                        <div
                            v-for="(item, hIndex) in section.highlights"
                            :key="hIndex"
                            class="rounded-[20px] border border-gray-100 bg-white p-8 shadow-sm"
                        >
                            <div
                                class="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-mm-green/10"
                            >
                                <span
                                    class="text-[16px] font-bold text-mm-green"
                                    >{{ hIndex + 1 }}</span
                                >
                            </div>
                            <h3
                                class="text-[20px] font-bold text-mm-dark"
                            >
                                {{ item.title }}
                            </h3>
                            <p
                                class="mt-2 text-[15px] font-medium leading-[24px] text-[#6d706d]"
                            >
                                {{ item.description }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </template>

        <!-- ==================== CTA SECTION ==================== -->
        <section v-if="ctaSection" class="bg-mm-dark py-20 lg:py-28">
            <div
                class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
            >
                <p
                    class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                >
                    {{ location.name }} Lawn Care
                </p>
                <h2
                    class="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
                >
                    {{ ctaSection.heading }}
                </h2>
                <div
                    class="prose mx-auto mt-6 max-w-xl prose-p:text-[18px] prose-p:leading-[30px] prose-p:font-medium prose-p:text-white/80"
                    v-html="ctaSection.body"
                />
                <Link
                    href="/get-started"
                    class="mt-8 inline-block cursor-pointer rounded-md bg-mm-green px-10 py-4 text-[16px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
                >
                    Get Started
                </Link>
            </div>
        </section>

        <!-- ==================== FAQs (matches Questions page) ==================== -->
        <section
            v-if="location.faqs?.length"
            aria-labelledby="faq-heading"
            class="bg-white py-20 lg:py-28"
        >
            <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                <div class="mb-12 text-center lg:mb-16">
                    <p
                        class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                    >
                        FAQ
                    </p>
                    <h2
                        id="faq-heading"
                        class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[70px] lg:leading-[88px]"
                    >
                        Frequently Asked {{ ' ' }}<br />Questions
                    </h2>
                </div>

                <dl class="divide-y divide-gray-200">
                    <div
                        v-for="(faq, index) in location.faqs"
                        :key="index"
                    >
                        <dt>
                            <button
                                :id="`faq-question-${index}`"
                                :aria-expanded="openFaqIndex === index"
                                :aria-controls="`faq-answer-${index}`"
                                class="group flex w-full cursor-pointer items-center justify-between py-6 text-left lg:py-7"
                                @click="toggleFaq(index)"
                            >
                                <span
                                    class="pr-6 text-[20px] font-bold text-mm-dark transition-colors group-hover:text-mm-green lg:text-[22px]"
                                >
                                    {{ faq.question }}
                                </span>
                                <PlusCircleIcon
                                    class="size-7 shrink-0 text-gray-400 transition-all duration-200 lg:size-8"
                                    :class="
                                        openFaqIndex === index
                                            ? 'rotate-45 text-mm-green'
                                            : ''
                                    "
                                    aria-hidden="true"
                                />
                            </button>
                        </dt>
                        <dd
                            :id="`faq-answer-${index}`"
                            :aria-labelledby="`faq-question-${index}`"
                            v-show="openFaqIndex === index"
                            class="pb-6 lg:pb-7"
                            role="region"
                        >
                            <p
                                class="text-[17px] font-medium leading-[28px] text-[#6d706d] lg:text-[18px]"
                            >
                                {{ faq.answer }}
                            </p>
                        </dd>
                    </div>
                </dl>
            </div>
        </section>

        <!-- ==================== SERVICE AREAS ==================== -->
        <section
            v-if="location.service_areas?.length"
            aria-labelledby="areas-heading"
            class="bg-mm-dark py-20 lg:py-28"
        >
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="mb-12 text-center lg:mb-16">
                    <p
                        class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                    >
                        Areas We Serve
                    </p>
                    <h2
                        id="areas-heading"
                        class="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-[70px] lg:leading-[88px]"
                    >
                        Serving {{ location.name }} {{ ' ' }}<br />& Surrounding Areas
                    </h2>
                </div>

                <div class="flex flex-wrap justify-center gap-3 lg:gap-4">
                    <span
                        v-for="area in location.service_areas"
                        :key="area"
                        class="rounded-full border border-white/20 bg-white/10 px-6 py-3 text-[16px] font-semibold text-white lg:text-[18px]"
                    >
                        {{ area }}
                    </span>
                </div>
            </div>
        </section>

        <!-- ==================== INTERNAL LINKS ==================== -->
        <section
            v-if="allInternalLinks.length"
            class="bg-mm-light py-16 lg:py-20"
        >
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="text-center">
                    <p
                        class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                    >
                        Explore More
                    </p>
                    <h2
                        class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl"
                    >
                        Our {{ location.name }} Services
                    </h2>
                    <div class="mt-8 flex flex-wrap justify-center gap-4">
                        <Link
                            v-for="slug in allInternalLinks"
                            :key="slug"
                            :href="`/${slug}`"
                            class="rounded-md border-2 border-mm-green px-6 py-3 text-[15px] font-bold tracking-[1.6px] text-mm-green uppercase transition-colors hover:bg-mm-green hover:text-white"
                        >
                            {{ slug.replace(/-/g, ' ') }}
                        </Link>
                    </div>
                </div>
            </div>
        </section>

        <SiteFooter />
    </div>
</template>
